import React, { useRef, useState, useEffect } from 'react';
import './style.css';

const ResizableTextarea = ({
  value,
  onChange,
  onKeyPress,
  placeholder = "Send a message",
  minWidth = 200,
  minHeight = 40,
  maxHeight = 300,
  rows = 1,
  resizeMode = 'both', // 'vertical' | 'horizontal' | 'both' | 'none'
  ...props
}) => {
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const resizerRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: minWidth,
    height: minHeight
  });
  const resizeDataRef = useRef({
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0
  });
  const manuallyResizedRef = useRef(false); // Відстежуємо чи користувач вручну змінював розмір

  // Автоматичне розширення висоти при введенні тексту
  const autoResize = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Якщо користувач вручну змінював розмір, не застосовуємо автоматичне розширення
    if (manuallyResizedRef.current) return;

    // Скидаємо висоту для правильного вимірювання
    textarea.style.height = 'auto';
    
    // Встановлюємо нову висоту на основі контенту
    const newHeight = Math.max(
      minHeight,
      Math.min(maxHeight, textarea.scrollHeight)
    );
    
    setDimensions(prev => ({
      ...prev,
      height: newHeight
    }));
  }, [minHeight, maxHeight]);

  // Обробка зміни тексту
  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
    autoResize();
  };

  // Початок зміни розміру
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Якщо режим 'none', не дозволяємо resize
    if (resizeMode === 'none') return;
    
    const textarea = textareaRef.current;
    if (!textarea) return;

    setIsResizing(true);
    
    const rect = textarea.getBoundingClientRect();
    resizeDataRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height
    };

    // Змінюємо курсор залежно від режиму
    let cursor = 'nw-resize';
    if (resizeMode === 'vertical') cursor = 'ns-resize';
    if (resizeMode === 'horizontal') cursor = 'ew-resize';
    
    document.body.style.cursor = cursor;
    document.body.style.userSelect = 'none';
  };

  // Процес зміни розміру
  const resize = React.useCallback((e) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeDataRef.current.startX;
    const deltaY = e.clientY - resizeDataRef.current.startY;
    
    // Обчислюємо нову ширину (тільки якщо дозволено horizontal або both)
    let newWidth = resizeDataRef.current.startWidth;
    if (resizeMode === 'horizontal' || resizeMode === 'both') {
      newWidth = Math.max(
        minWidth,
        resizeDataRef.current.startWidth + deltaX
      );
    }
    
    // Обчислюємо нову висоту (тільки якщо дозволено vertical або both)
    // Інвертуємо вертикальне розтягування: віднімаємо deltaY
    let newHeight = resizeDataRef.current.startHeight;
    if (resizeMode === 'vertical' || resizeMode === 'both') {
      newHeight = Math.max(
        minHeight,
        Math.min(maxHeight, resizeDataRef.current.startHeight - deltaY)
      );
    }
    
    setDimensions({
      width: newWidth,
      height: newHeight
    });
  }, [isResizing, minWidth, minHeight, maxHeight, resizeMode]);

  // Закінчення зміни розміру
  const stopResize = React.useCallback(() => {
    if (!isResizing) return;
    
    setIsResizing(false);
    
    // Позначаємо, що користувач вручну змінив розмір
    manuallyResizedRef.current = true;
    
    // Відновлюємо курсор
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing]);

  // Додаємо глобальні слухачі для зміни розміру
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
      
      return () => {
        document.removeEventListener('mousemove', resize);
        document.removeEventListener('mouseup', stopResize);
      };
    }
  }, [isResizing, resize, stopResize]);

  // Автоматичне розширення при зміні value ззовні
  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  return (
    <div 
      ref={containerRef}
      className="resizable-textarea-container"
    >
      <textarea
        ref={textareaRef}
        className="resizable-textarea"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyPress={onKeyPress}
        rows={rows}
        style={{
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
          minWidth: `${minWidth}px`,
          minHeight: `${minHeight}px`,
          maxHeight: `${maxHeight}px`
        }}
        {...props}
      />
      {resizeMode !== 'none' && (
        <div
          ref={resizerRef}
          className={`resizable-textarea-resizer resizable-textarea-resizer-${resizeMode} ${isResizing ? 'resizing' : ''}`}
          onMouseDown={startResize}
        >
          <div className="resizable-textarea-resizer-indicator" />
        </div>
      )}
    </div>
  );
};

export default ResizableTextarea;
